import { useQuery } from "@tanstack/react-query";
import {
  getAllPOAStatementConsentsRecommendations,
  getPoaJbsData,
  getPOARecommendations,
  getPOARikServiceProvider,
  getPOASignedDocument,
  getPOASignedDocumentMetadata,
  getPOAStatementConsentsBanks,
  getPOAStatementConsentsRecommendations,
  getPOAStatementConsentsState,
  getPOAStatementConsentsStatus,
  getUnderpermissionedExistingConsents,
} from "./services";
import {
  type IPOARecommendationsRequest,
  type IPOARikServiceProviderRequest,
  type IPOAStatementConsentsRecommendationsRequest,
  type IPOAStatementConsentsStatusRequest,
} from "./types";

export const usePOARecommendationsQuery = (data: IPOARecommendationsRequest) =>
  useQuery({
    queryKey: ["powersOfAttorney", "recommendations", data.jointBillServiceId],
    queryFn: () => getPOARecommendations(data),
    enabled: !!data.jointBillServiceId,
  });

export const usePOAStatementConsentsBanksQuery = () =>
  useQuery({
    queryKey: ["powersOfAttorney", "statementConsents", "banks"],
    queryFn: getPOAStatementConsentsBanks,
  });

export const usePOAStatementConsentsStateQuery = (enabled?: boolean) =>
  useQuery({
    queryKey: ["powersOfAttorney", "statementConsents", "state"],
    queryFn: getPOAStatementConsentsState,
    enabled,
  });

export const usePOAStatementConsentsStatusQuery = (
  data: IPOAStatementConsentsStatusRequest,
  enabled?: boolean,
) =>
  useQuery({
    queryKey: [
      "powersOfAttorney",
      "statementConsents",
      "status",
      data.powerOfAttorneyLedgerConsentId,
    ],
    queryFn: () => getPOAStatementConsentsStatus(data),
    enabled,
  });

export const usePOAStatementConsentsRecommendationsQuery = (
  data: IPOAStatementConsentsRecommendationsRequest,
) =>
  useQuery({
    queryKey: [
      "powersOfAttorney",
      "statementConsents",
      "recommendations",
      data.powerOfAttorneyLedgerConsentId,
      data.jointBillServiceId,
    ],
    queryFn: () => getPOAStatementConsentsRecommendations(data),
    enabled: !!data.jointBillServiceId,
  });

export const usePoaJbsDataQuery = (enabled?: boolean) =>
  useQuery({
    queryKey: ["powersOfAttorney", "serviceProviderRequests"],
    queryFn: () => getPoaJbsData(),
    enabled,
  });

export const usePOASignedDocumentMetadataQuery = (enabled?: boolean) =>
  useQuery({
    queryKey: ["powersOfAttorney", "signedDocumentMetadata"],
    queryFn: () => getPOASignedDocumentMetadata(),
    enabled,
  });

export const usePOARikServiceProviderQuery = (
  data: IPOARikServiceProviderRequest,
) =>
  useQuery({
    queryKey: [
      "powersOfAttorney",
      "rikServiceProvider",
      data.serviceProviderSearchWord,
    ],
    queryFn: () => getPOARikServiceProvider(data),
    enabled: !!data.serviceProviderSearchWord.length,
  });

export const usePOASignedDocumentQuery = () =>
  useQuery({
    queryKey: ["powersOfAttorney", "signedDocument"],
    queryFn: () => getPOASignedDocument(),
  });

export const useUnderpermissionedExistingConsentsQuery = (enabled: boolean) =>
  useQuery({
    queryKey: ["powersOfAttorney", "underpermissionedExistingConsents"],
    queryFn: () => getUnderpermissionedExistingConsents(),
    enabled,
  });

export const useAllPOAStatementConsentsRecommendationsQuery = (
  data: IPOAStatementConsentsRecommendationsRequest,
) =>
  useQuery({
    queryKey: [
      "powersOfAttorney",
      "statementConsents",
      "recommendations",
      "all",
      data.jointBillServiceId,
    ],
    queryFn: () =>
      getAllPOAStatementConsentsRecommendations(data.jointBillServiceId),
    enabled: !!data.jointBillServiceId,
  });
